import React from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { ArrowDownIcon, ArrowUpIcon } from "../../../icons";
import { Icon } from "@chakra-ui/icons";
import { useBooleanState } from "../../../hooks";
import { TableContainerProps, TableThProps } from "./Table.types";
import { TdProps } from "../VeriDataTable/Table";

export const Container: React.FC<TableContainerProps> = ({
  children,
  variant = "default",
  ...rest
}) => {
  return (
    <Box
      display="block"
      w="100%"
      overflowX="auto"
      borderRadius={4}
      border={variant === "list" ? "none" : "1px"}
      boxShadow={variant === "list" ? "none" : "0 1px 5px 0 rgba(0, 0, 0, 0.1)"}
      borderColor="gray.200"
      {...rest}
    >
      {children}
    </Box>
  );
};

export const Table: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Box
      as="table"
      w="100%"
      table-layout="auto"
      border-collapse="chevronUp"
      {...rest}
    >
      {children}
    </Box>
  );
};

export const Thead = React.forwardRef<HTMLDivElement, BoxProps>(
  ({ children, ...rest }, ref) => {
    return (
      <Box as="thead" p={4} textAlign="left" {...rest} ref={ref}>
        {children}
      </Box>
    );
  },
);

Thead.displayName = "Thead";

export const Tbody: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Box as="tbody" p={4} {...rest}>
      {children}
    </Box>
  );
};

export const Tfoot: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Box as="tfoot" p={4} {...rest}>
      {children}
    </Box>
  );
};

export const Tr: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Box as="tr" my={1} {...rest}>
      {children}
    </Box>
  );
};

export const Th: React.FC<TableThProps> = ({
  children,
  sortable,
  sort,
  onClick,
  align,
  ...rest
}) => {
  const isHover = useBooleanState(false);
  const clickable = ((isHover.state && sortable) || onClick) && true;
  return (
    <Box
      as="th"
      p={"12px"}
      fontSize="14px"
      borderBottom="1px"
      borderBottomColor="gray.200"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      _hover={{
        cursor: clickable ? "pointer" : "",
        color: clickable && "text.grey",
      }}
      onClick={onClick}
      onMouseOver={isHover.true.bind(null)}
      onMouseOut={isHover.false.bind(null)}
      textAlign={align}
      {...rest}
    >
      {sortable && sort === "asc" && (
        <Icon
          as={ArrowUpIcon}
          boxSize="16px"
          mr="4px"
          color="text.grey"
          mt="-4px"
        />
      )}
      {sortable && sort === "desc" && (
        <Icon
          as={ArrowDownIcon}
          boxSize="16px"
          mr="4px"
          color="text.grey"
          mt="-4px"
        />
      )}
      {isHover.state && sortable && !sort && (
        <Icon
          as={ArrowUpIcon}
          boxSize="16px"
          color="text.grey"
          mr="4px"
          mt="-4px"
        />
      )}
      {children}
    </Box>
  );
};

export const Td: React.FC<BoxProps & { colSpan?: TdProps["colSpan"] }> = ({
  children,
  onClick,
  ...rest
}) => (
  <Box
    as="td"
    p={"12px"}
    borderBottom="1px"
    fontSize="14px"
    borderBottomColor="gray.200"
    _hover={{ cursor: onClick ? "pointer" : "" }}
    onClick={onClick}
    {...rest}
  >
    {children}
  </Box>
);

export default {
  Container,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
};
